// import { Button } from "@workspace/web-ui/components/button";
import { Trash2, Circle} from "lucide-react";
import { cn } from "@workspace/web-ui/lib/utils";
import { AppointmentModal } from "../../pages/patient-logs/components/appointment-modal";
import { useState } from "react";

import { Patient, PatientSessions } from "../../pages/patient-logs/api/types";
// import { toast } from "sonner";
import { useTask } from "./hook/use-task";
import { FormattedEncounter } from "../../pages/encounters/types";
import { formatEncounterFullDateTime } from "@workspace/lib/date";
import DosespotButton from "../../pages/patient-logs/pages/dosespotModal";
// import { useQueryClient } from "@tanstack/react-query";
// import { tasksKeys } from "../../api/tasks/keys";

type SessionType = PatientSessions['sessions'][0];

type UrgentCareTaskButtonsProps = {
    onAssignMedication?: () => void;
    onAssignAppointment?: () => void;
    className?: string;
    selectedSession?: SessionType;
    encounter?: FormattedEncounter;
    userDoseSpotId?: string;
    patient?: Patient
    // Add assignedTasks prop to check for existing appointment tasks
    assignedTasks?: Array<{
        task_id: string;
        type: string;
        status: string;
        created_at: string;
        cost: number;
        prescription?: {
            prescription_id: number;
            program: string;
            medication: Array<{
                name: string;
                dosage: string;
                duration: number;
                instructions: string;
                refills: string;
                effective_date: string;
            }>;
        };
        instructions?: string;
    }>;
};

export const UrgentCareTaskButtons = ({
    className,
    selectedSession,
    encounter,
    patient,
    userDoseSpotId,
    assignedTasks = [] // Default to empty array
}: UrgentCareTaskButtonsProps) => {
    const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
    
    // Check if there's an existing appointment followup task
    const hasAppointmentTask = assignedTasks.some(task => 
        task.type === "appointment_followup" && 
        task.status !== "completed" // Assuming you might have a status field
    );

    const urgentCareOnboarded = encounter?.patient?.urgent_care_onboarded ?? patient?.urgent_care_onboarded ?? false;
    const identityVerified = encounter?.patient?.identity_verified ?? patient?.identity_verified ?? false;

    return (
        <div className={cn("space-y-4", className)}>
            {/* Recommend Actions Section */}
            <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="text-lg font-bold text-black mb-3">Recommend Actions</h3>
                <div className="flex flex-col w-full gap-3">
                    <div className="w-full">
                        <DosespotButton
                            userDosespotId={encounter?.patient.user_dosespot_id || userDoseSpotId}
                            sessionId={encounter?.session?.session_id || selectedSession?.session_id}
                            encounterId={encounter?.encounterId || selectedSession?.encounter?.encounter_id}
                            urgent_care_onboarded={true}
                            // Disable if appointment task exists or patient not onboarded
                            disabled={hasAppointmentTask || !urgentCareOnboarded || !identityVerified}
                            onSuccess={() => {}}
                        />
                    </div>
                    {/* <Button
                        onClick={() => setIsAppointmentModalOpen(true)}
                        className="bg-[#00C781] w-full hover:bg-[#00C781] border-[#00C781] text-white px-4 py-2 rounded flex items-center gap-2 h-[44px]"
                        disabled={!urgentCareOnboarded}
                    >
                        <Calendar className="w-4 h-4" />
                        Assign Appointment
                    </Button> */}
                </div>
                
                {/* Optional: Show message when medication button is disabled due to appointment */}
                {hasAppointmentTask && (
                    <div className="mt-2 text-sm text-orange-600 bg-orange-50 p-2 rounded">
                        Prescribing medication is disabled because an appointment has been assigned.
                    </div>
                )}
            </div>
            
            {/* Appointment Modal */}
            <AppointmentModal
                isOpen={isAppointmentModalOpen}
                onClose={() => setIsAppointmentModalOpen(false)}
                selectedSession={selectedSession}
                encounter={encounter}
            />
        </div>
    );
};

// Task Card Component for Assigned Actions
type TaskCardProps = {
    taskId: string;
    type: string;
    assignedDate: string;
    price?: number;
    instructions?: string;
    medications?: Array<{
        name: string;
        dosage: string;
        duration: number;
        instructions: string;
        refill?: number;
        effectiveDate?: string;
    }>;
    onDelete?: (id: string) => void;
    isSelected?: boolean;
    selectedSession?: SessionType;
    onSelect?: (id: string) => void;
};

export const UrgentCareTaskCard = ({
    taskId,
    type,
    assignedDate,
    instructions,
    price,
    selectedSession,
    medications,
    onDelete,
}: TaskCardProps) => (
    <div className="bg-[#F1F4F9] border border-gray-200 rounded-lg p-4 flex flex-col items-start justify-between">
        <div className="flex items-start justify-between gap-3 flex-1 w-full">
            <div className="flex items-center gap-4">
                {selectedSession?.treatment_completed ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="24" height="24" rx="12" fill="#00C781"/>
                    <path d="M16.8327 8.25L10.8743 14.2083L8.16602 11.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    
                ) : (
                    <Circle className="text-[#4272DD] size-[20px]" />
                )}
                <div className="flex-1">
                    <h4
                        className={`font-medium ${selectedSession?.treatment_completed ? "text-[#00C781] line-through" : "text-black"}`}
                    >
                        {type === "appointment_followup" ? "Pay for appointment" : "Prescribed Medication"}
                    </h4>
                    <p className="text-sm text-gray-500">Assigned: {formatEncounterFullDateTime(assignedDate)}</p>
                </div>
            </div>
            {type !== "medication_payment" && (
                <button
                    onClick={() => onDelete?.(taskId)}
                    className="text-gray-400 hover:text-red-500 p-1"
                    aria-label="Delete task"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
        
        {type === "appointment_followup" && (
        <div className="bg-white rounded-[12px] p-4 w-full mt-4">
                <p className="text-sm text-gray-700 mt-1">Price: {price}</p>
                <p className="text-sm text-gray-700 mt-1">Instructions: {instructions}</p>
        </div>
        )}
        {type === "medication_payment" && medications && medications.length > 0 && (
                <div className="space-y-4 mt-4 w-full">
                    {medications.map((medication, index) => (
                        <div key={index} className="border-b bg-white rounded-[12px] p-4 w-full border-gray-100 last:border-b-0 pb-3">
                            <h5 className="font-medium text-gray-900 mb-4">{medication.name}</h5>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                                <p className="font-medium"><span className="font-normal text-[#202020CC]">Dosage:</span> {medication.dosage}</p>
                                <p className="font-medium"><span className="font-normal text-[#202020CC]">Duration:</span> {medication.duration}</p>
                                {medication.refill !== undefined && (
                                    <p className="font-medium"><span className="font-normal text-[#202020CC]">Refill:</span> {medication.refill}</p>
                                )}
                                {medication.effectiveDate && (
                                    <p className="font-medium"><span className="font-normal text-[#202020CC]">Effective date:</span> {formatEncounterFullDateTime(medication.effectiveDate)}</p>
                                )}
                            </div>
                            <p className="text-sm text-gray-700 mt-4 font-medium">
                                <span className="font-normal">Instructions:</span> {medication.instructions}
                            </p>
                        </div>
                    ))}
                </div>
        )}
    </div>
);

// Assigned Actions Container
type AssignedActionsProps = {
    tasks?: Array<{
        task_id: string;
        type: string;
        status: string;
        created_at: string;
        cost: number;
        prescription?: {
            prescription_id: number;
            program: string;
            medication: Array<{
                name: string;
                dosage: string;
                duration: number;
                instructions: string;
                refills: string;
                effective_date: string;
            }>;
        };
        instructions?: string;
    }>;

    encounterId: string
    onDeleteTask?: (id: string) => void;
    onSelectTask?: (id: string) => void;
    selectedTaskId?: string;
    selectedSession?: SessionType;
    className?: string;
};

const TaskCardWithDelete = ({ 
    task, 
    selectedSession, 
    onSelectTask, 
    selectedTaskId 
}: {
    task: any;
    selectedSession?: SessionType;
    onSelectTask?: (id: string) => void;
    selectedTaskId?: string;
}) => {
    // Each task gets its own useTask hook with the specific task ID
    const { onDeleteTask } = useTask({
        id: selectedSession?.encounter?.encounter_id ?? "",
        taskId: task.task_id // Use this specific task's ID
    });

    const handleDelete = async () => {
        try {
            await onDeleteTask();
            // Add your success handling here
        } catch (error) {
            console.error("Error deleting task:", error);
            // Add your error handling here
        }
    };

    return (
        <UrgentCareTaskCard
            taskId={task.task_id}
            type={task.type}
            assignedDate={task.created_at}
            selectedSession={selectedSession}
            medications={task.prescription?.medication}
            price={task.cost}
            instructions={task.instructions}
            onDelete={() => handleDelete()} // Call the delete function directly
            onSelect={onSelectTask}
            isSelected={selectedTaskId === task.task_id}
        />
    );
};

export const AssignedActions = ({
    tasks,
    // onTaskDeleted,
    // encounterId,
    selectedSession,
    onSelectTask,
    selectedTaskId,
    className
}: AssignedActionsProps) => {
    // const queryClient = useQueryClient()

    // const handleDeleteTask = async () => {
    //     try {
    //         await onDeleteTask();

    //         toast.success('Task deleted successfully', {
    //             style: {
    //                 background: "#10B981",
    //                 color: "#FFFFFF",
    //                 borderColor: "#10B981",
    //                 '& svg': {
    //                     color: '#FFFFFF'
    //                 }
    //             } as React.CSSProperties
    //         });

    //         queryClient.invalidateQueries({ queryKey: tasksKeys.getTasksByEncounterId(encounterId ?? "") });

    //         // if (onTaskDeleted) onTaskDeleted();

    //         return;
    //     } catch (error) {
    //         console.error("Error deleting task:", error);
    //         return toast.error(
    //             'Failed to delete the task.',
    //             {
    //                 style: {
    //                     background: '#DC2626',
    //                     color: '#FFFFFF',
    //                     borderColor: '#DC2626',
    //                     '& svg': {
    //                         color: '#FFFFFF'
    //                     }
    //                 } as React.CSSProperties
    //             }
    //         );
    //     }
    // }
    return (
    <div className={cn("space-y-3", className)}>
        <h3 className="text-lg font-bold text-black">Assigned actions</h3>
        <div className="space-y-3">
        {tasks?.map((task) => (
                    <TaskCardWithDelete
                        key={task.task_id}
                        task={task}
                        selectedSession={selectedSession}
                        onSelectTask={onSelectTask}
                        selectedTaskId={selectedTaskId}
                    />
                ))}
            {tasks?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    <p>No assigned actions yet</p>
                </div>
            )}
        </div>
    </div>
    )
};
